import { Cron } from 'croner'
import moment from 'moment'

// Helpers for the etcd backup ClusterAddon (type etcd-backup). The rules mirror the kaapi CRD in
// barista/api/v1alpha1/clusteraddon_types.go, and the status text comes from
// barista/internal/controller/clusteraddon_etcdbackup.go.

// EBK-FR-001: type and version of the add-on
export const ETCD_BACKUP_ADDON_TYPE = 'etcd-backup'
export const ETCD_BACKUP_ADDON_VERSION = '1.0.0'
export const ETCD_BACKUP_CREATE_FAILED_MESSAGE = 'Failed to create the Backup add-on'

// EBK-FR-002: defaults, the same as the CRD's
export const ETCD_BACKUP_DEFAULT_STORAGE_PATH = '/etc/pf9/etcd-backup'
export const ETCD_BACKUP_DEFAULT_TIME_ZONE = 'UTC'
export const ETCD_BACKUP_DEFAULT_MAX_COUNT = 7
export const ETCD_BACKUP_MIN_COUNT = 1
export const ETCD_BACKUP_MAX_COUNT = 10

// EBK-FR-015: the schedule names, in the order the form shows and the body sends them
export type EtcdBackupScheduleName = 'hourly' | 'daily' | 'weekly' | 'custom'
export const ETCD_BACKUP_SCHEDULE_NAMES: EtcdBackupScheduleName[] = [
  'hourly',
  'daily',
  'weekly',
  'custom',
]
export const ETCD_BACKUP_SCHEDULE_LABELS: Record<EtcdBackupScheduleName, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  custom: 'Custom',
}

export const ETCD_BACKUP_INITIAL_VALUES = {
  etcdBackup: false,
  etcdBackupStoragePath: '',
  etcdBackupTimeZone: '',
  etcdBackupSchedules: ['daily'] as EtcdBackupScheduleName[],
  etcdBackupHourlyMinute: '',
  etcdBackupHourlyMaxCount: '',
  etcdBackupDailyTime: '',
  etcdBackupDailyMaxCount: '',
  etcdBackupWeeklyDay: '',
  etcdBackupWeeklyTime: '',
  etcdBackupWeeklyMaxCount: '',
  etcdBackupCustomSchedule: '',
  etcdBackupCustomMaxCount: '',
}

// A type, not an interface, so it fits useParams' Record<string, unknown> constraint
export type EtcdBackupFormParams = {
  etcdBackup?: boolean
  etcdBackupStoragePath?: string
  etcdBackupTimeZone?: string
  // The schedules the user added
  etcdBackupSchedules?: EtcdBackupScheduleName[]
  etcdBackupHourlyMinute?: string | number
  etcdBackupHourlyMaxCount?: string | number
  etcdBackupDailyTime?: string
  etcdBackupDailyMaxCount?: string | number
  // Day of the week, 0 (Sunday) to 6, as cron numbers it
  etcdBackupWeeklyDay?: string
  etcdBackupWeeklyTime?: string
  etcdBackupWeeklyMaxCount?: string | number
  etcdBackupCustomSchedule?: string
  etcdBackupCustomMaxCount?: string | number
}

export interface EtcdBackupSchedule {
  name: EtcdBackupScheduleName
  schedule: string
  maxCount: number
}

// One time zone for every schedule; each schedule keeps its own backups.
export interface EtcdBackupConfig {
  storagePath: string
  timeZone: string
  schedules: EtcdBackupSchedule[]
}

// The newest successful backup, across the schedules, from status.lastBackup
export interface EtcdBackupLastBackup {
  time: string
  schedule: string
  path?: string
  node?: string
}

// EBK-FR-019: the soonest backup to come, across the schedules, from getNextEtcdBackup
export interface EtcdBackupNextRun {
  time: string
  schedule: string
}

export interface ClusterAddonStatus {
  status?: string
  failureReason?: string
  failureMessage?: string
  message?: string
  lastBackup?: EtcdBackupLastBackup
}

const isBlank = (value?: string | number | null) =>
  value === undefined || value === null || String(value).trim() === ''

// EBK-FR-003: storage path rules, from the CRD's storagePath validations
const PROTECTED_PATHS = [
  '/etc/kubernetes',
  '/var/lib/etcd',
  '/var/lib/kubelet',
  '/proc',
  '/sys',
  '/dev',
  '/tmp',
  '/run',
  '/var/run',
]
const STORAGE_PATH_PATTERN = /^\/[A-Za-z0-9._/-]+$/
const STORAGE_PATH_MAX_LENGTH = 255

export const getStoragePathError = (value?: string): string | null => {
  if (isBlank(value)) return null
  const path = String(value).trim()
  if (!path.startsWith('/')) return 'Enter an absolute path, starting with /'
  if (path.length > STORAGE_PATH_MAX_LENGTH) {
    return `The path must be at most ${STORAGE_PATH_MAX_LENGTH} characters`
  }
  if (!STORAGE_PATH_PATTERN.test(path)) {
    return 'Use only letters, digits, dots, underscores, hyphens and slashes'
  }
  if (path.includes('..')) return "The path must not contain '..'"
  if (path.includes('//') || path.includes('/./') || path.endsWith('/.')) {
    return "The path must not contain '//' or a '.' segment"
  }
  const protectedPath = PROTECTED_PATHS.find((p) => path === p || path.startsWith(`${p}/`))
  if (protectedPath) {
    return `The path must not be inside ${PROTECTED_PATHS.slice(0, -1).join(', ')} or ${
      PROTECTED_PATHS[PROTECTED_PATHS.length - 1]
    }`
  }
  return null
}

// EBK-FR-004: daily time as HH:MM, 00:00 to 23:59
const DAILY_TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/
export const isValidDailyTime = (value?: string) =>
  !isBlank(value) && DAILY_TIME_PATTERN.test(String(value).trim())

// EBK-FR-005: max count is empty (default) or a whole number from 1 to 10
export const isValidMaxCount = (value?: string | number) => {
  if (isBlank(value)) return true
  const text = String(value).trim()
  if (!/^[0-9]+$/.test(text)) return false
  const count = Number(text)
  return count >= ETCD_BACKUP_MIN_COUNT && count <= ETCD_BACKUP_MAX_COUNT
}

// EBK-FR-006: the minute of the hour for hourly backups, 0 to 59
export const isValidMinute = (value?: string | number) => {
  if (isBlank(value)) return false
  const text = String(value).trim()
  return /^[0-9]{1,2}$/.test(text) && Number(text) <= 59
}

// EBK-FR-016: the days for weekly backups, numbered as cron numbers them
export const etcdBackupWeekdayOptions = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
].map((label, day) => ({ label, value: String(day) }))

// EBK-FR-014: a custom schedule is 5 cron fields, the CRD's pattern. barista checks the fields
// and that runs are at least 5 minutes apart, and reports a bad one as InvalidConfiguration.
const normalizeSchedule = (value?: string) =>
  String(value ?? '')
    .trim()
    .split(/\s+/)
    .join(' ')
export const isValidCronSchedule = (value?: string) =>
  !isBlank(value) && /^[^\s@]+( \S+){4}$/.test(normalizeSchedule(value))

// EBK-FR-007: UTC first, then the IANA zones this browser knows
export const getTimeZoneOptions = (): { label: string; value: string }[] => {
  const supported =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  const zones = [ETCD_BACKUP_DEFAULT_TIME_ZONE, ...supported.filter((z) => z !== 'UTC')]
  return zones.map((zone) => ({ label: zone, value: zone }))
}

// EBK-FR-008: the backend needs at least one schedule
export const ETCD_BACKUP_NO_SCHEDULE_MESSAGE = 'Add at least one backup schedule'

export const getEtcdBackupSettingsError = (params: EtcdBackupFormParams): string | null =>
  params.etcdBackupSchedules?.length ? null : ETCD_BACKUP_NO_SCHEDULE_MESSAGE

const toCount = (value?: string | number) =>
  isBlank(value) ? ETCD_BACKUP_DEFAULT_MAX_COUNT : Number(String(value).trim())

// "HH:MM" as cron's minute and hour fields, without leading zeros
const timeToCron = (value?: string) => {
  const [hour, minute] = String(value).trim().split(':')
  return `${Number(minute)} ${Number(hour)}`
}

const scheduleFor = (name: EtcdBackupScheduleName, params: EtcdBackupFormParams) => {
  switch (name) {
    case 'hourly':
      return `${Number(String(params.etcdBackupHourlyMinute).trim())} * * * *`
    case 'daily':
      return `${timeToCron(params.etcdBackupDailyTime)} * * *`
    case 'weekly':
      return `${timeToCron(params.etcdBackupWeeklyTime)} * * ${params.etcdBackupWeeklyDay}`
    default:
      return normalizeSchedule(params.etcdBackupCustomSchedule)
  }
}

const MAX_COUNT_PARAMS: Record<EtcdBackupScheduleName, keyof EtcdBackupFormParams> = {
  hourly: 'etcdBackupHourlyMaxCount',
  daily: 'etcdBackupDailyMaxCount',
  weekly: 'etcdBackupWeeklyMaxCount',
  custom: 'etcdBackupCustomMaxCount',
}

// EBK-FR-009: spec.clusterSpec.etcdBackup from the form, with defaults for empty fields. Each
// schedule becomes a cron schedule in the addon's time zone.
export const buildEtcdBackupClusterSpec = (
  params: EtcdBackupFormParams,
): { etcdBackup: EtcdBackupConfig } => {
  const added = params.etcdBackupSchedules ?? []
  return {
    etcdBackup: {
      storagePath: isBlank(params.etcdBackupStoragePath)
        ? ETCD_BACKUP_DEFAULT_STORAGE_PATH
        : String(params.etcdBackupStoragePath).trim(),
      timeZone: isBlank(params.etcdBackupTimeZone)
        ? ETCD_BACKUP_DEFAULT_TIME_ZONE
        : String(params.etcdBackupTimeZone),
      schedules: ETCD_BACKUP_SCHEDULE_NAMES.filter((name) => added.includes(name)).map((name) => ({
        name,
        schedule: scheduleFor(name, params),
        maxCount: toCount(params[MAX_COUNT_PARAMS[name]] as string | number | undefined),
      })),
    },
  }
}

// EBK-FR-013: form values from a saved add-on, for the configure drawer. A value equal to its
// default is left empty, so it shows as the placeholder as it does in the create form. A
// schedule that doesn't have its type's shape, such as one edited with kubectl, leaves its
// fields empty for the user to fill in again.
interface SavedEtcdBackupConfig {
  storagePath?: string
  timeZone?: string
  schedules?: { name?: string; schedule?: string; maxCount?: number }[]
}

const countOrBlank = (count?: number) =>
  count === undefined || count === ETCD_BACKUP_DEFAULT_MAX_COUNT ? '' : String(count)

const pad = (value: string) => value.padStart(2, '0')
const HOURLY_SHAPE = /^([0-9]{1,2}) \* \* \* \*$/
const DAILY_SHAPE = /^([0-9]{1,2}) ([0-9]{1,2}) \* \* \*$/
const WEEKLY_SHAPE = /^([0-9]{1,2}) ([0-9]{1,2}) \* \* ([0-6])$/

const scheduleParams = (name: string, schedule = ''): EtcdBackupFormParams => {
  if (name === 'hourly') {
    const match = schedule.match(HOURLY_SHAPE)
    return { etcdBackupHourlyMinute: match ? match[1] : '' }
  }
  if (name === 'daily') {
    const match = schedule.match(DAILY_SHAPE)
    return { etcdBackupDailyTime: match ? `${pad(match[2])}:${pad(match[1])}` : '' }
  }
  if (name === 'weekly') {
    const match = schedule.match(WEEKLY_SHAPE)
    return match
      ? { etcdBackupWeeklyDay: match[3], etcdBackupWeeklyTime: `${pad(match[2])}:${pad(match[1])}` }
      : {}
  }
  return { etcdBackupCustomSchedule: schedule }
}

// EBK-FR-018: a saved schedule in words for the Backup details, such as "Every Sunday at
// 03:00". A custom schedule, or one without its type's shape, is shown as its cron schedule.
export const describeEtcdBackupSchedule = (
  name: string,
  schedule = '',
  timeZone?: string,
): string => {
  // A daily or weekly time is read in the add-on's time zone, so it is named
  const zone = timeZone ? ` ${getTimeZoneShortName(getEtcdBackupTimeZone(timeZone))}` : ''
  const time = (hour: string, minute: string) => `${pad(hour)}:${pad(minute)}${zone}`
  const hourly = name === 'hourly' && schedule.match(HOURLY_SHAPE)
  if (hourly) return `Every hour at minute ${hourly[1]}`
  const daily = name === 'daily' && schedule.match(DAILY_SHAPE)
  if (daily) return `Every day at ${time(daily[2], daily[1])}`
  const weekly = name === 'weekly' && schedule.match(WEEKLY_SHAPE)
  if (weekly) {
    return `Every ${etcdBackupWeekdayOptions[Number(weekly[3])].label} at ${time(weekly[2], weekly[1])}`
  }
  return schedule
}

export const etcdBackupFormParamsFromSpec = (
  config?: SavedEtcdBackupConfig,
): EtcdBackupFormParams => {
  if (!config) return { ...ETCD_BACKUP_INITIAL_VALUES }
  const { storagePath, timeZone } = config
  const saved = (config.schedules ?? []).filter((s) =>
    ETCD_BACKUP_SCHEDULE_NAMES.includes(s.name as EtcdBackupScheduleName),
  )
  const params: EtcdBackupFormParams = {
    ...ETCD_BACKUP_INITIAL_VALUES,
    etcdBackup: true,
    etcdBackupStoragePath:
      storagePath === ETCD_BACKUP_DEFAULT_STORAGE_PATH ? '' : storagePath || '',
    etcdBackupTimeZone: timeZone === ETCD_BACKUP_DEFAULT_TIME_ZONE ? '' : timeZone || '',
    etcdBackupSchedules: saved.map((s) => s.name as EtcdBackupScheduleName),
  }
  for (const s of saved) {
    Object.assign(params, scheduleParams(s.name as string, s.schedule), {
      [MAX_COUNT_PARAMS[s.name as EtcdBackupScheduleName]]: countOrBlank(s.maxCount),
    })
  }
  return params
}

// EBK-FR-010: a failure message as one line per schedule. Barista joins each schedule's
// problem with "; ", and each starts with the schedule's name. Another add-on's message
// stays one line.
const SCHEDULE_BOUNDARY = /; (?=(?:hourly|daily|weekly|custom): )/

export const splitFailureMessage = (message?: string): string[] =>
  isBlank(message) ? [] : String(message).split(SCHEDULE_BOUNDARY)

// EBK-FR-017: a zone's short name, such as IST, EDT, CET or GMT. Browsers name a zone in some
// locales only, so this tries a few. A zone with no name shows its UTC offset, such as UTC+8
// or UTC+5:45.
const ZONE_NAME_LOCALES = ['en-US', 'en-GB', 'en-IN']
const OFFSET_NAME = /^GMT[+-]/

// timeZoneName: 'short' always gives a timeZoneName part
const zoneName = (timeZone: string, at: Date, locale: string) =>
  (
    new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName') as Intl.DateTimeFormatPart
  ).value

export const getTimeZoneShortName = (timeZone: string, at: Date = new Date()): string => {
  const names = ZONE_NAME_LOCALES.map((locale) => zoneName(timeZone, at, locale))
  const named = names.find((name) => !OFFSET_NAME.test(name))
  // en-US gives the offset as GMT+8 when it has no name
  return named ?? names[0].replace(/^GMT/, 'UTC')
}

// A zone's UTC offset at a time, such as UTC+5:30, UTC-4, or UTC at offset zero. en-US writes
// it as GMT+5:30, and offset zero as GMT or GMT+0.
export const getUTCOffset = (timeZone: string, at: Date = new Date()): string => {
  const offset = (
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName') as Intl.DateTimeFormatPart
  ).value
  return offset.replace(/^GMT([+-]0)?$/, 'UTC').replace(/^GMT/, 'UTC')
}

// A zone's name and UTC offset, or one of them when the other adds nothing: [IST, UTC+5:30],
// [UTC+8] for a zone with no name, [UTC] or [GMT] at offset zero.
const zoneParts = (timeZone: string, at: Date): string[] => {
  const name = getTimeZoneShortName(timeZone, at)
  const offset = getUTCOffset(timeZone, at)
  return name === offset || ['UTC', 'GMT'].includes(name) ? [name] : [name, offset]
}

// EBK-FR-017: a zone as the backend's status names it, such as "IST (UTC+5:30)", "UTC+8" for
// a zone with no name, or "UTC"
export const getTimeZoneLabel = (timeZone: string, at: Date = new Date()): string => {
  const [name, offset] = zoneParts(timeZone, at)
  return offset ? `${name} (${offset})` : name
}

// BDM-FR-005: the add-on's time zone with its name and offset, such as
// "Asia/Kolkata (IST, UTC+5:30)", or just "UTC"
export const describeTimeZone = (timeZone: string, at: Date = new Date()): string => {
  const parts = zoneParts(timeZone, at)
  return parts.length === 1 && parts[0] === timeZone
    ? timeZone
    : `${timeZone} (${parts.join(', ')})`
}

// The time zone the add-on's schedules run in, UTC when it has none or the browser doesn't
// know it
export const getEtcdBackupTimeZone = (timeZone?: string): string => {
  if (!timeZone) return ETCD_BACKUP_DEFAULT_TIME_ZONE
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return timeZone
  } catch {
    return ETCD_BACKUP_DEFAULT_TIME_ZONE
  }
}

// A zone's UTC offset at a time, in minutes: 330 for UTC+5:30, -240 for UTC-4
const getUTCOffsetMinutes = (timeZone: string, at: Date): number => {
  const match = getUTCOffset(timeZone, at).match(/^UTC([+-])(\d+)(?::(\d+))?$/)
  if (!match) return 0
  const minutes = Number(match[2]) * 60 + Number(match[3] ?? 0)
  return match[1] === '-' ? -minutes : minutes
}

// When a clock goes back, the wall time of a run comes round again. The CronJob runs at both,
// so the second one is a run too: the same wall time, later by the hour the clock went back.
const repeatedRun = (timeZone: string, run: Date): Date | null => {
  const offset = getUTCOffsetMinutes(timeZone, run)
  const back = offset - getUTCOffsetMinutes(timeZone, new Date(run.getTime() + 3 * 3600_000))
  if (back <= 0) return null
  const repeat = new Date(run.getTime() + back * 60_000)
  return getUTCOffsetMinutes(timeZone, repeat) === offset - back ? repeat : null
}

const MAX_RUNS_CHECKED = 1000

// EBK-FR-019: a schedule's next run after a time, as the Kubernetes CronJob runs it (robfig/cron):
// at every moment the zone's wall clock matches. croner moves a time a clock skips to after the
// jump, and runs a time a clock repeats once, so a run that doesn't match is skipped and a
// repeated time counts twice. null when the schedule doesn't parse or never runs.
export const getNextScheduleRun = (
  schedule: string,
  timeZone: string,
  after: Date,
): Date | null => {
  let cron: Cron
  try {
    cron = new Cron(schedule, { timezone: timeZone })
  } catch {
    return null
  }
  // Start 3 hours early, so a repeat of an earlier run is found too.
  let run = cron.nextRun(new Date(after.getTime() - 3 * 3600_000))
  let next: Date | null = null
  for (let i = 0; run && i < MAX_RUNS_CHECKED; i++) {
    if (cron.match(run)) {
      const repeat = repeatedRun(timeZone, run)
      for (const candidate of [run, repeat]) {
        if (candidate && candidate > after && (!next || candidate < next)) next = candidate
      }
      // A later run's repeat is later still.
      if (run > after) break
    }
    run = cron.nextRun(run)
  }
  return next
}

// EBK-FR-019: the soonest backup to come across a saved add-on's schedules, in its time zone,
// with the schedule that runs it. The first schedule wins a tie, as in barista. null when no
// schedule runs, or the browser doesn't know the zone.
export const getNextEtcdBackup = (
  schedules?: { name?: string; schedule?: string }[],
  timeZone?: string,
  now: Date = new Date(),
): EtcdBackupNextRun | null => {
  const zone = getEtcdBackupTimeZone(timeZone)
  if (timeZone && zone !== timeZone) return null
  let next: { at: Date; schedule: string } | null = null
  for (const { name, schedule } of schedules ?? []) {
    if (!name || !isValidCronSchedule(schedule)) continue
    const at = getNextScheduleRun(normalizeSchedule(schedule), zone, now)
    if (at && (!next || at < next.at)) next = { at, schedule: name }
  }
  return next && { time: next.at.toISOString(), schedule: next.schedule }
}

// EBK-FR-019: barista deploys a saved add-on's schedules unless it rejected them. Then the
// CronJobs still run the schedules from before, so the next run can't be worked out from the
// spec.
const SCHEDULES_NOT_DEPLOYED_REASONS = ['InvalidConfiguration', 'UnsupportedKubernetesVersion']

export const areEtcdBackupSchedulesDeployed = (status?: ClusterAddonStatus): boolean =>
  !SCHEDULES_NOT_DEPLOYED_REASONS.includes(status?.failureReason ?? '')

// EBK-FR-017: a backup time in the add-on's time zone, with the zone's name and offset, such as
// "29 Oct 2026, 20:15 IST (UTC+5:30)"
export const formatBackupTime = (time: string, timeZone?: string): string => {
  const zone = getEtcdBackupTimeZone(timeZone)
  const at = new Date(time)
  const local = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at)
  return `${local} ${getTimeZoneLabel(zone, at)}`
}

// EBK-FR-017: a backup run, last or next, for the UI: how long ago or until, the time in the
// add-on's time zone, and the schedule's label
export const formatBackupRun = (run: { time: string; schedule: string }, timeZone?: string) => ({
  relative: moment(run.time).fromNow(),
  exact: formatBackupTime(run.time, timeZone),
  schedule: ETCD_BACKUP_SCHEDULE_LABELS[run.schedule as EtcdBackupScheduleName] ?? run.schedule,
})

// EBK-FR-011: what each failure reason means
const FAILURE_REASON_EXPLANATIONS: Record<string, string> = {
  BackupFailed:
    "A backup run failed, a run's pods didn't start within 15 minutes, or no backup finished in time.",
  DeployFailed: "The backup couldn't be deployed to the cluster.",
  InvalidConfiguration:
    "The settings aren't valid for this cluster: it isn't a colocated control plane cluster, the time zone is unknown, or a schedule isn't valid or runs less than 5 minutes apart.",
  UnsupportedKubernetesVersion:
    "Backups aren't available yet for this cluster's Kubernetes version.",
}

export const getFailureReasonExplanation = (reason?: string): string | null =>
  (reason && FAILURE_REASON_EXPLANATIONS[reason]) || null

// EBK-FR-012: readable add-on names
const ADDON_DISPLAY_NAMES: Record<string, string> = {
  [ETCD_BACKUP_ADDON_TYPE]: 'Backup',
  metallb: 'MetalLB',
  'metrics-server': 'Metrics Server',
  'cinder-csi': 'Cinder CSI',
  'cert-manager': 'cert-manager',
  calico: 'Calico',
  cilium: 'Cilium',
}

export const getAddonDisplayName = (type?: string): string =>
  (type && ADDON_DISPLAY_NAMES[type]) || type || ''
